import { useCallback } from "react";
import {
  UsageLog,
  UsageStatus,
  useCheckInMutation,
  useFinishUsageMutation,
  useGetUsageLogQuery,
} from "../../../api/usageLog/usageLogApiSlice";

/**
 * Derive the lifecycle status from the stored event timestamps, never from the
 * returned `status` string or a client flag — timestamps are the source of
 * truth (canonical model: `docs/architecture/check-in-check-out-flow.md`). The
 * most-advanced stamp wins, so a stale value can't race a refetch. This returns
 * `booked | active | finished` only; **overtime** is derived separately by the
 * caller (it needs the order's scheduled end-time — see `util/overtime.ts`).
 */
export const deriveUsageStatus = (
  usageLog: UsageLog | undefined
): UsageStatus => {
  if (!usageLog) return "booked";
  if (usageLog.finishedAtDate) return "finished";
  if (usageLog.activatedAtDate) return "active";
  return "booked";
};

export interface UseUsageLifecycleResult {
  status: UsageStatus;
  usageLog: UsageLog | undefined;
  isLoading: boolean;
  checkIn: (participantCount?: number | null) => Promise<UsageLog>;
  finishUsage: (notes?: string | null) => Promise<UsageLog>;
}

/**
 * Wraps the usage_log query + the two lifecycle mutations (check-in, finish)
 * for a single order. Status is always derived from the returned timestamps.
 * After each mutation, tag invalidation (`["UsageLog"]`) refetches the query, so
 * callers never have to manually re-sync.
 */
export const useUsageLifecycle = (
  orderId: string
): UseUsageLifecycleResult => {
  const { data: usageLog, isLoading } = useGetUsageLogQuery(
    { orderId },
    { skip: !orderId }
  );
  const [checkInMutation] = useCheckInMutation();
  const [finishUsageMutation] = useFinishUsageMutation();

  const checkIn = useCallback(
    (participantCount?: number | null) =>
      checkInMutation({ orderId, participantCount }).unwrap(),
    [checkInMutation, orderId]
  );

  const finishUsage = useCallback(
    (notes?: string | null) =>
      finishUsageMutation({ orderId, notes }).unwrap(),
    [finishUsageMutation, orderId]
  );

  return {
    status: deriveUsageStatus(usageLog),
    usageLog,
    isLoading,
    checkIn,
    finishUsage,
  };
};
